import axios from 'axios';

const DEFAULT_BACKEND_URL = 'http://localhost:8000';
const RAW_API_URL = (process.env.REACT_APP_API_URL || DEFAULT_BACKEND_URL).trim();
const URL_WITH_PROTOCOL = /^https?:\/\//i.test(RAW_API_URL)
  ? RAW_API_URL
  : `http://${RAW_API_URL}`;
export const BACKEND_BASE_URL = URL_WITH_PROTOCOL.replace(/\/$/, '');
const API_BASE_URL = URL_WITH_PROTOCOL.endsWith('/api')
  ? URL_WITH_PROTOCOL
  : `${BACKEND_BASE_URL}/api`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: API_KEY ? { 'X-API-Key': API_KEY } : {},
});

// Add token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }

  // Attach org tenant code when available (before or after login)
  const orgCode = localStorage.getItem('orgCode');
  if (orgCode) {
    config.headers['X-Tenant-Code'] = orgCode;
  }

  // Only set Content-Type to JSON if data is not FormData
  if (!(config.data instanceof FormData) && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }

  return config;
});

// Handle responses — sync token expiry from every API response
apiClient.interceptors.response.use(
  (response) => {
    const expiresAt = response.headers['x-token-expires-at'];
    const requestToken = (response.config?.headers?.Authorization || '').replace(/^Token\s+/i, '');
    if (expiresAt && requestToken && requestToken === localStorage.getItem('token')) {
      localStorage.setItem('tokenExpiresAt', expiresAt);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Only force a logout if this 401 belongs to the token we're
      // currently using. Without this check, a stale request made with an
      // old/expired token (e.g. the auth-check fired on page load) can
      // resolve *after* the user has already logged in again with a fresh
      // token, wiping out the brand-new valid session and bouncing them
      // straight back to the login screen.
      const requestAuthHeader = error.config?.headers?.Authorization || '';
      const requestToken = requestAuthHeader.replace(/^Token\s+/i, '');
      const currentToken = localStorage.getItem('token');
      if (requestToken && requestToken === currentToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('tokenExpiresAt');
        // Keep in sync with AuthContext.logout()'s cleanup — otherwise a
        // stale org code survives here and ProtectedRoute's cross-org guard
        // mistakes the next legitimate login for a cross-org URL change,
        // wiping the fresh token and bouncing straight back to /login.
        localStorage.removeItem('_loginOrg');
        const pathOrg = window.location.pathname.split('/')[1];
        const orgCode = pathOrg && !['login', 'register', 'forgot-password', 'superadmin', 'privacy', 'support'].includes(pathOrg.toLowerCase())
          ? pathOrg : localStorage.getItem('orgCode');
        window.location.href = orgCode ? `/${orgCode.toLowerCase()}/login` : '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;